import {
  validateFileSecure,
  ALLOWED_FILE_TYPES as UPLOAD_ALLOWED,
  getExtension,
} from "./fileUploadUtils";

const fileUtils = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB

  // Advertised types (help text). Runtime FileAttachment still enforces PDF via fileUploadUtils.
  ALLOWED_FILE_TYPES: {
    pdf: "application/pdf",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    txt: "text/plain",
  },

  formatFileSize: (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  },

  validateFile: async (file) => {
    // Use secure validation; allow advertised types when called directly
    return validateFileSecure(
      file,
      fileUtils.ALLOWED_FILE_TYPES,
      fileUtils.MAX_FILE_SIZE
    );
  },

  fileToBase64: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        // Extract only the base64 part from the data URL
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  },

  getFileExtension: (filename) => {
    return getExtension(filename);
  },

  getFileIconAndClass: (filename) => {
    const extension = fileUtils.getFileExtension(filename);

    switch (extension) {
      case "pdf":
        return { icon: "bi-file-earmark-pdf", colorClass: "file-pdf" };
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return { icon: "bi-file-earmark-image", colorClass: "file-image" };
      case "doc":
      case "docx":
        return { icon: "bi-file-earmark-word", colorClass: "file-doc" };
      case "xls":
      case "xlsx":
        return { icon: "bi-file-earmark-excel", colorClass: "file-excel" };
      case "ppt":
      case "pptx":
        return { icon: "bi-file-earmark-ppt", colorClass: "file-ppt" };
      case "zip":
      case "rar":
        return { icon: "bi-file-earmark-zip", colorClass: "file-zip" };
      default:
        return { icon: "bi-file-earmark-text", colorClass: "file-other" };
    }
  },

  filesToAttachments: async (files) => {
    if (!files || files.length === 0) {
      return [];
    }

    const attachments = [];

    for (const file of files) {
      if (!(file instanceof Blob)) {
        console.warn("Invalid file object:", file);
        continue;
      }

      try {
        // Re-validate before encoding (blocks spoofed content at submit)
        const check = await validateFileSecure(
          file,
          UPLOAD_ALLOWED,
          fileUtils.MAX_FILE_SIZE
        );
        if (!check.isValid) {
          throw new Error(check.message);
        }

        const fileBase64 = await fileUtils.fileToBase64(file);
        const safeMime =
          UPLOAD_ALLOWED[check.extension] ||
          fileUtils.ALLOWED_FILE_TYPES[check.extension] ||
          "application/octet-stream";

        attachments.push({
          src: `data:${safeMime};base64,${fileBase64}`,
          desc: "",
          name: file.name,
          att_id: "200",
          doc_no: "PPOOAB",
        });
      } catch (error) {
        console.error("Error converting file to base64:", error);
        throw error;
      }
    }

    return attachments;
  },
};

export default fileUtils;
